// frontend/src/pages/CalendarioPage.jsx

import React, { useState, useEffect } from 'react';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import './CalendarStyles.css';
import api from '../services/api';
import toast from 'react-hot-toast';
import { formatDateISO, isSameDay } from '../utils/dateUtils';
import { User, Clock, CheckCircle2, Circle, CalendarClock } from 'lucide-react';

export default function CalendarioPage() {
  const [dataSelecionada, setDataSelecionada] = useState(new Date());
  const [sessoes, setSessoes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [abaAtiva, setAbaAtiva] = useState('dia'); // 'dia' | 'nao-agendadas'

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [sessionToEdit, setSessionToEdit] = useState(null);
  const [newDate, setNewDate] = useState('');
  const [newTime, setNewTime] = useState('');

  useEffect(() => {
    buscarTodasSessoes();
  }, []);

  const buscarTodasSessoes = async () => {
    try {
      setLoading(true);
      const response = await api.get('/sessions');
      setSessoes(response.data.data);
    } catch (error) {
      toast.error('Erro ao carregar sessões');
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = (session) => {
    setSessionToEdit(session);
    setNewDate(formatDateISO(session.sessionDate) || '');
    setNewTime(session.sessionTime || '09:00');
    setIsModalOpen(true);
  };

  const closeEditModal = () => {
    setIsModalOpen(false);
    setSessionToEdit(null);
  };

  const handleUpdateSession = async () => {
    if (!sessionToEdit || !newDate || !newTime) {
      toast.error('Preencha a data e a hora.');
      return;
    }

    const promise = api.put(`/sessions/${sessionToEdit._id}`, {
      sessionDate: newDate,
      sessionTime: newTime,
    });

    toast.promise(promise, {
      loading: 'Atualizando sessão...',
      success: 'Sessão agendada com sucesso!',
      error: 'Falha ao atualizar a sessão.'
    });

    try {
      await promise;
      closeEditModal();
      buscarTodasSessoes();
      setAbaAtiva('dia');
    } catch (error) {}
  };

  const tileContent = ({ date, view }) => {
    if (view === 'month') {
      const sessoesDoDia = sessoes.filter(s => s.sessionDate && isSameDay(s.sessionDate, date));
      if (sessoesDoDia.length === 0) return null;
      const temAvaliada = sessoesDoDia.some(s => s.hasEvolution);
      const temPendente = sessoesDoDia.some(s => !s.hasEvolution);
      return (
        <div className="flex justify-center items-center gap-0.5 mt-1">
          {temAvaliada && <span className="w-2 h-2 rounded-full bg-green-500 inline-block"></span>}
          {temPendente && <span className="w-2 h-2 rounded-full bg-blue-500 inline-block"></span>}
        </div>
      );
    }
    return null;
  };

  const sessoesDoDiaSelecionado = sessoes.filter(s => s.sessionDate && isSameDay(s.sessionDate, dataSelecionada));
  const sessoesNaoAgendadas = sessoes.filter(s => !s.sessionDate);
  const avaliadasHoje = sessoesDoDiaSelecionado.filter(s => s.hasEvolution).length;
  const pendentesHoje = sessoesDoDiaSelecionado.filter(s => !s.hasEvolution).length;
  const totalAvaliadas = sessoes.filter(s => s.hasEvolution).length;
  const totalPendentes = sessoes.filter(s => s.sessionDate && !s.hasEvolution).length;

  const renderCardSessao = (sessao) => (
    <div
      key={sessao._id}
      onClick={() => openEditModal(sessao)}
      className={`p-4 rounded-xl border cursor-pointer transition-all duration-200
        ${sessao.hasEvolution
          ? 'border-green-200 bg-green-50 hover:border-green-300 hover:bg-green-100'
          : !sessao.sessionDate
          ? 'border-orange-200 bg-orange-50 hover:border-orange-300 hover:bg-orange-100'
          : 'border-gray-100 bg-white hover:border-blue-200 hover:bg-blue-50'
        }`}
    >
      <div className="flex items-start gap-3">
        {sessao.sessionTime && (
          <p className={`font-bold text-base leading-tight flex-shrink-0 ${sessao.hasEvolution ? 'text-green-600' : 'text-blue-600'}`}>
            {sessao.sessionTime}
          </p>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 justify-between">
            <div className="flex items-center gap-2">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0
                ${sessao.hasEvolution ? 'bg-green-100' : !sessao.sessionDate ? 'bg-orange-100' : 'bg-blue-100'}`}>
                <User className={`w-3 h-3 ${sessao.hasEvolution ? 'text-green-600' : !sessao.sessionDate ? 'text-orange-500' : 'text-blue-600'}`} />
              </div>
              <p className="font-semibold text-gray-900 text-sm truncate">
                {sessao.patient?.nome || 'Paciente'}
              </p>
            </div>
            {sessao.hasEvolution
              ? <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
              : !sessao.sessionDate
              ? <CalendarClock className="w-4 h-4 text-orange-400 flex-shrink-0" />
              : <Circle className="w-4 h-4 text-gray-300 flex-shrink-0" />
            }
          </div>
          <p className="text-xs text-gray-500 mt-1 ml-8">
            Sessão #{sessao.sessionNumber}
            {sessao.patient?.diagnosisTitle && <span> • {sessao.patient.diagnosisTitle}</span>}
          </p>
          <p className={`text-xs mt-1 ml-8 font-medium
            ${sessao.hasEvolution ? 'text-green-600' : !sessao.sessionDate ? 'text-orange-500' : 'text-gray-400'}`}>
            {sessao.hasEvolution ? 'Avaliação registrada' : !sessao.sessionDate ? 'Clique para agendar' : 'Pendente de avaliação'}
          </p>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Calendário de Sessões</h1>
        <p className="text-gray-500 mt-1">Gerencie e visualize todas as sessões agendadas</p>
      </div>

      {loading && (
        <div className="flex justify-center mb-4">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">

        {/* Calendário */}
        <div className="lg:col-span-2 bg-white rounded-2xl shadow-sm border border-gray-100 p-6 flex flex-col" style={{ minHeight: '520px' }}>
          <Calendar
            onChange={setDataSelecionada}
            value={dataSelecionada}
            tileContent={tileContent}
            className="w-full border-0 flex-1"
          />
          <div className="mt-4 pt-4 border-t border-gray-100 flex items-center gap-6 text-sm text-gray-600">
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-green-500 inline-block"></span>
              <span>{totalAvaliadas} avaliada{totalAvaliadas !== 1 ? 's' : ''}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-blue-500 inline-block"></span>
              <span>{totalPendentes} pendente{totalPendentes !== 1 ? 's' : ''}</span>
            </div>
            {sessoesNaoAgendadas.length > 0 && (
              <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-orange-400 inline-block"></span>
                <span>{sessoesNaoAgendadas.length} sem data</span>
              </div>
            )}
          </div>
        </div>

        {/* Painel lateral */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6" style={{ minHeight: '520px' }}>

          {/* Abas */}
          <div className="flex gap-1 bg-gray-100 rounded-xl p-1 mb-4">
            <button
              onClick={() => setAbaAtiva('dia')}
              className={`flex-1 text-xs font-medium py-2 rounded-lg transition-all ${abaAtiva === 'dia' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
            >
              {dataSelecionada.toLocaleDateString('pt-BR', { day: 'numeric', month: 'short' })}
            </button>
            <button
              onClick={() => setAbaAtiva('nao-agendadas')}
              className={`flex-1 text-xs font-medium py-2 rounded-lg transition-all flex items-center justify-center gap-1.5 ${abaAtiva === 'nao-agendadas' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
            >
              Sem data
              {sessoesNaoAgendadas.length > 0 && (
                <span className={`text-xs px-1.5 py-0.5 rounded-full font-semibold ${abaAtiva === 'nao-agendadas' ? 'bg-orange-100 text-orange-600' : 'bg-gray-200 text-gray-500'}`}>
                  {sessoesNaoAgendadas.length}
                </span>
              )}
            </button>
          </div>

          {/* Aba: sessões do dia */}
          {abaAtiva === 'dia' && (
            <>
              <div className="flex items-center gap-2 mb-3">
                {avaliadasHoje > 0 && (
                  <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">
                    {avaliadasHoje} avaliada{avaliadasHoje !== 1 ? 's' : ''}
                  </span>
                )}
                {pendentesHoje > 0 && (
                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">
                    {pendentesHoje} pendente{pendentesHoje !== 1 ? 's' : ''}
                  </span>
                )}
              </div>
              <div className="space-y-3 max-h-[380px] overflow-y-auto pr-1">
                {sessoesDoDiaSelecionado.length > 0 ? (
                  sessoesDoDiaSelecionado.map(renderCardSessao)
                ) : (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-3">
                      <Clock className="w-6 h-6 text-gray-400" />
                    </div>
                    <p className="text-sm text-gray-500">Nenhuma sessão neste dia</p>
                    <p className="text-xs text-gray-400 mt-1">Veja a aba "Sem data" para agendar</p>
                  </div>
                )}
              </div>
              {sessoesDoDiaSelecionado.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <p className="text-xs text-gray-400 text-center">Clique em uma sessão para editar</p>
                </div>
              )}
            </>
          )}

          {/* Aba: sessões sem data */}
          {abaAtiva === 'nao-agendadas' && (
            <div className="space-y-3 max-h-[400px] overflow-y-auto pr-1">
              {sessoesNaoAgendadas.length > 0 ? (
                sessoesNaoAgendadas.map(renderCardSessao)
              ) : (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mb-3">
                    <CheckCircle2 className="w-6 h-6 text-green-500" />
                  </div>
                  <p className="text-sm text-gray-700 font-medium">Tudo agendado!</p>
                  <p className="text-xs text-gray-400 mt-1">Todas as sessões têm data marcada</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-40">
          <div className="bg-white p-6 rounded-2xl shadow-xl w-full max-w-md mx-4">
            <h2 className="text-xl font-bold text-gray-900 mb-1">Agendar Sessão</h2>
            <p className="mb-4 text-gray-600 text-sm">
              Paciente: <span className="font-semibold text-gray-900">{sessionToEdit?.patient?.nome}</span>
              <span className="text-gray-400"> · Sessão {sessionToEdit?.sessionNumber}</span>
              {sessionToEdit?.hasEvolution && (
                <span className="ml-2 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">Avaliada</span>
              )}
            </p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Data</label>
                <input
                  type="date"
                  value={newDate}
                  onChange={(e) => setNewDate(e.target.value)}
                  className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Hora</label>
                <input
                  type="time"
                  value={newTime}
                  onChange={(e) => setNewTime(e.target.value)}
                  className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                />
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={closeEditModal} className="px-4 py-2.5 border border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors text-sm font-medium">
                Cancelar
              </button>
              <button onClick={handleUpdateSession} className="px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-colors text-sm font-medium">
                Salvar Agendamento
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}